import { format } from 'date-fns'
import {
  Activity,
  BarChart3,
  Filter,
  MousePointerClick,
  Percent,
  Search,
  TrendingDown,
  TrendingUp,
  Users
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { StatTile } from '@features/dashboard/components/StatTile'
import {
  clickThroughRateValue,
  eventCount,
  eventsPerSessionValue,
  percentChange
} from '@services/event-analytics/trends'
import type { EventAnalyticsSummary } from '@services/event-analytics/types'

import { cn } from '@/lib/utils'

interface EventAnalyticsKpiCardsProps {
  summary?: EventAnalyticsSummary
  previousSummary?: EventAnalyticsSummary
  loading?: boolean
}

function formatEventsPerSession(summary?: EventAnalyticsSummary): string {
  const value = eventsPerSessionValue(summary)
  if (value === null) return '—'
  return value.toFixed(1)
}

function formatClickThroughRate(summary?: EventAnalyticsSummary): string {
  const value = clickThroughRateValue(summary)
  if (value === null) return '—'
  return `${value.toFixed(1)} %`
}

function formatTrendPercent(change: number | null): string | null {
  if (change === null) return null
  const sign = change >= 0 ? '+' : ''
  return `${sign}${change.toFixed(0)} %`
}

function KpiValueWithTrend({
  value,
  trend
}: {
  value: ReactNode
  trend: number | null
}) {
  const { t } = useTranslation('eventAnalytics')
  const trendLabel = formatTrendPercent(trend)

  return (
    <div>
      <div className="font-display text-xl font-bold leading-tight text-ink">
        {value}
      </div>
      {trendLabel ? (
        <div
          className={cn(
            'mt-0.5 flex items-center gap-1 text-xs font-medium',
            trend !== null && trend > 0
              ? 'text-success'
              : trend !== null && trend < 0
                ? 'text-destructive'
                : 'text-muted-foreground'
          )}
        >
          {trend !== null && trend > 0 ? (
            <TrendingUp className="h-3 w-3" />
          ) : trend !== null && trend < 0 ? (
            <TrendingDown className="h-3 w-3" />
          ) : (
            <span className="h-3 w-3 text-center leading-none">–</span>
          )}
          <span>
            {trendLabel} {t('kpi.vsPrevious')}
          </span>
        </div>
      ) : null}
    </div>
  )
}

export function EventAnalyticsKpiCards({
  summary,
  previousSummary,
  loading = false
}: EventAnalyticsKpiCardsProps) {
  const { t } = useTranslation('eventAnalytics')

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl bg-ink/[0.04]" />
        ))}
      </div>
    )
  }

  const byEvent = summary?.by_event ?? {}

  const trends = {
    totalEvents: percentChange(
      summary?.total_events ?? 0,
      previousSummary?.total_events ?? 0
    ),
    uniqueSessions: percentChange(
      summary?.unique_sessions ?? 0,
      previousSummary?.unique_sessions ?? 0
    ),
    eventsPerSession: (() => {
      const cur = eventsPerSessionValue(summary)
      const prev = eventsPerSessionValue(previousSummary)
      if (cur === null || prev === null) return null
      return percentChange(cur, prev)
    })(),
    ctr: (() => {
      const cur = clickThroughRateValue(summary)
      const prev = clickThroughRateValue(previousSummary)
      if (cur === null || prev === null) return null
      return percentChange(cur, prev)
    })(),
    filters: percentChange(
      eventCount(summary, 'filters_applied'),
      eventCount(previousSummary, 'filters_applied')
    ),
    viewed: percentChange(
      eventCount(summary, 'apartment_viewed'),
      eventCount(previousSummary, 'apartment_viewed')
    ),
    clicked: percentChange(
      eventCount(summary, 'apartment_clicked'),
      eventCount(previousSummary, 'apartment_clicked')
    ),
    chat: percentChange(
      eventCount(summary, 'chat_search'),
      eventCount(previousSummary, 'chat_search')
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        icon={Activity}
        tone="teal"
        label={t('kpi.totalEvents')}
        value={
          <KpiValueWithTrend
            value={summary?.total_events ?? 0}
            trend={trends.totalEvents}
          />
        }
      />
      <StatTile
        icon={Users}
        tone="blue"
        label={t('kpi.uniqueSessions')}
        value={
          <KpiValueWithTrend
            value={summary?.unique_sessions ?? 0}
            trend={trends.uniqueSessions}
          />
        }
      />
      <StatTile
        icon={TrendingUp}
        tone="purple"
        label={t('kpi.eventsPerSession')}
        value={
          <KpiValueWithTrend
            value={formatEventsPerSession(summary)}
            trend={trends.eventsPerSession}
          />
        }
      />
      <StatTile
        icon={Percent}
        tone="amber"
        label={t('kpi.clickThroughRate')}
        value={
          <KpiValueWithTrend
            value={formatClickThroughRate(summary)}
            trend={trends.ctr}
          />
        }
      />
      <StatTile
        icon={Filter}
        tone="purple"
        label={t('kpi.filtersApplied')}
        value={
          <KpiValueWithTrend
            value={byEvent.filters_applied ?? 0}
            trend={trends.filters}
          />
        }
      />
      <StatTile
        icon={BarChart3}
        tone="amber"
        label={t('kpi.apartmentViewed')}
        value={
          <KpiValueWithTrend
            value={byEvent.apartment_viewed ?? 0}
            trend={trends.viewed}
          />
        }
      />
      <StatTile
        icon={MousePointerClick}
        tone="green"
        label={t('kpi.apartmentClicked')}
        value={
          <KpiValueWithTrend
            value={byEvent.apartment_clicked ?? 0}
            trend={trends.clicked}
          />
        }
      />
      <StatTile
        icon={Search}
        tone="red"
        label={t('kpi.chatSearch')}
        value={
          <KpiValueWithTrend
            value={byEvent.chat_search ?? 0}
            trend={trends.chat}
          />
        }
      />
    </div>
  )
}

export function formatEventAnalyticsPeriod(
  from?: string,
  to?: string
): string | null {
  if (!from || !to) return null
  try {
    return `${format(new Date(from), 'd.M.yyyy')} – ${format(new Date(to), 'd.M.yyyy')}`
  } catch {
    return null
  }
}
